/**
 * The scorecard, evaluated one month at a time.
 *
 * All seven period rules now have a monthly twin, so a month is scored out of the
 * same 11 as the period. Collection rate was the exception until 2026-09-22: it was
 * thought to need cash, which this lakehouse has none of. It does not — the semantic
 * model defines `Collected Percentage` as resolved charge over total charge, which
 * FactClaimAllocations carries at claim grain and therefore at every grain above it.
 * MONTH_ONLY_GAPS is computed rather than asserted, so if a rule ever loses its
 * monthly twin the UI says so on its own.
 *
 * Three of the six compare against a PER-MONTH peer average and three against the
 * period one, and the split is not arbitrary:
 *
 *  - Cost per claim and claims-billed-vs-resolved are rates. A rate is comparable
 *    across months, so the period average is the stabler bar and a thin month does
 *    not get scored against its own noise.
 *  - Claim count is a volume, so the period average is divided by the number of
 *    months to put both sides on a monthly footing.
 *  - 0% touch share, open claims worked and facilities handled move with the month
 *    itself — a slow August drags everyone's figure down together — so they compare
 *    against the cohort's average FOR THAT MONTH. Scoring them against a period
 *    average would hand out points for the calendar rather than for the work.
 *
 * A rule with no figure for that person-month scores nothing and is reported as a
 * gap, not silently dropped: a month missing three measures must not read like a
 * month that genuinely earned less.
 *
 * THE −3 PENALTY applies month by month too (since 2026-10-21, monthly scorecard
 * statements): a month whose claims billed vs resolved % is under 50% loses 3 points, as
 * the period score does. Until then a month's floor was 0 and a penalty month could read
 * +3.5 in the grid and +0.5 on the statement (MEASURE-RULES §10.8, inconsistency 1).
 *
 * BELOW MINIMUM BILLING VOLUME (review #695 as rewritten 2026-10-18): a month billing
 * under its job title's minimum that month (Person.volumeBars, measures/minimumVolume.ts)
 * scores 0 here (`got`), with what it would have scored kept as `lowVolume.computed`; the
 * month grid shows such a month as Not rated and leaves it out of the row average (#985).
 * Those people
 * and person-months are left out of the bars everyone else is scored against, as
 * terminated people are.
 */
import type { Person } from '@/data/selectors'
import { divide, touchBase } from './core'
import { COLLECTION_LABEL } from './collection'
import { SCORE_RULES, CLAIM_FLOOR, PENALTY, isLowVolume, type LowVolume, type ScoreKey } from './scorecard'
import { billedOf, type VolumeBar } from './minimumVolume'
import type { TouchMonthRow } from '@/data/types'

/** How each monthly rule gets its value and which bar it is measured against. */
interface MonthRule {
  key: ScoreKey
  label: string
  pts: number
  lowerIsBetter: boolean
  /** The person-month value. */
  get: (m: TouchMonthRow) => number | null
  /** 'month' = cohort average for that month; 'period' = the period peer average. */
  bar: 'month' | 'period'
  /** Divide the period bar by the month count — for volumes, not rates. */
  perMonth?: boolean
  /** The period-level value, for building the period bar. */
  periodValue?: (p: Person) => number | null
}

const pts = (k: ScoreKey) => SCORE_RULES.find((r) => r.key === k)!.pts

export const MONTH_RULES: MonthRule[] = [
  {
    key: 'costPerClaim',
    label: 'Cost per claim',
    pts: pts('costPerClaim'),
    lowerIsBetter: true,
    get: (m) => divide(m.claimLabor, m.billedClaims),
    bar: 'period',
    periodValue: (p) => p.costPerClaim,
  },
  {
    key: 'claims',
    label: 'Billed claims',
    pts: pts('claims'),
    lowerIsBetter: false,
    get: (m) => m.billedClaims || null,
    bar: 'period',
    perMonth: true,
    periodValue: (p) => p.claims,
  },
  {
    key: 'closedClaimPct',
    label: 'Claims billed vs resolved %',
    pts: pts('closedClaimPct'),
    lowerIsBetter: false,
    get: (m) => divide(m.resolvedClaims, m.billedClaims),
    bar: 'period',
    periodValue: (p) => p.closedClaimPct,
  },
  {
    key: 'collectedPct',
    label: COLLECTION_LABEL,
    pts: pts('collectedPct'),
    lowerIsBetter: false,
    // A rate, so it compares against the period bar like the other two rates. The
    // month's own six-month window, three months back (MEASURE-RULES §4.3); the bar is
    // the peers' range windows.
    get: (m) => m.collectedPct,
    bar: 'period',
    periodValue: (p) => p.collectedPct,
  },
  {
    key: 'zeroTouchShare',
    label: '0% touch share',
    pts: pts('zeroTouchShare'),
    lowerIsBetter: false,
    // Over the touch buckets' own sum, never `resolvedClaims` (a different date basis).
    get: (m) => divide(m.resolvedNoTouch, touchBase(m)),
    bar: 'month',
  },
  {
    key: 'openWorkedPct',
    label: 'Open claims worked in month %',
    pts: pts('openWorkedPct'),
    lowerIsBetter: false,
    get: (m) => divide(m.openClaimsWorked, m.openClaims),
    bar: 'month',
  },
  {
    key: 'facilities',
    label: 'Facilities handled',
    pts: pts('facilities'),
    lowerIsBetter: false,
    get: (m) => m.facilities || null,
    bar: 'month',
  },
]

/**
 * The most a single month can be worth: 11, the period's maximum, since collection rate
 * got its monthly twin (2026-09-22). The floor is −3, as the period's: the penalty
 * applies month by month since 2026-10-21 (`monthPenalty`). MEASURE-RULES §10.8.
 */
export const MONTH_MAX = MONTH_RULES.reduce((s, r) => s + r.pts, 0)

/** Rules the period scorecard has and a month cannot. */
export const MONTH_ONLY_GAPS = SCORE_RULES.filter(
  (r) => !MONTH_RULES.some((m) => m.key === r.key),
).map((r) => r.label)

export interface MonthScore {
  /** Points earned. */
  got: number
  /** Points that were LIVE — rules this person-month had a figure for. */
  live: number
  /** Rules with no figure this month. */
  gaps: string[]
  /** Claims billed vs resolved % under 50% that month: `got` includes the −3. */
  penalty?: boolean
  /** The month billed under 25% of its claim bar: `got` is 0, `computed` what it earned. */
  lowVolume?: LowVolume
}

const mean = (xs: number[]): number | null =>
  xs.length === 0 ? null : xs.reduce((a, b) => a + b, 0) / xs.length

const peerKey = (p: Person) => `${p.department} | ${p.jobTitle}`
const peerKeyShore = (p: Person) => `${p.department} | ${p.jobTitle} | ${p.shore ?? '?'}`

/**
 * Peer averages for a population, built once and reused for every cell of the grid.
 *
 * Built on the UNFILTERED people, for the same reason cohorts are: slicing to one
 * department must not quietly redefine who a person is measured against. Terminated
 * people are dropped from the bars and get no month scores, as they get no period
 * score (DECISION-LOG.md §8).
 */
export function monthScorer(people: readonly Person[], months: readonly string[]) {
  const active = people.filter((p) => p.claims >= CLAIM_FLOOR && !p.terminated)
  // The low-volume test's bar is over every active peer; the bars everyone is scored
  // against leave the low-volume people out (`derive` marks them on their score).
  // `score` may be absent on a hand-built Person (tests): not low volume.
  const base = active.filter((p) => !p.score?.lowVolume)

  // Period bars, one per peer key. Cost per claim also keyed by shore.
  const periodBar = new Map<string, Map<ScoreKey, number | null>>()
  const shoreBar = new Map<string, number | null>()

  const group = <K extends string>(keyOf: (p: Person) => K) => {
    const g = new Map<K, Person[]>()
    for (const p of base) {
      const k = keyOf(p)
      const list = g.get(k)
      if (list) list.push(p)
      else g.set(k, [p])
    }
    return g
  }

  for (const [k, members] of group(peerKey)) {
    const m = new Map<ScoreKey, number | null>()
    for (const rule of MONTH_RULES) {
      if (rule.bar !== 'period' || !rule.periodValue) continue
      m.set(
        rule.key,
        mean(members.map(rule.periodValue).filter((v): v is number => v !== null)),
      )
    }
    periodBar.set(k, m)
  }
  for (const [k, members] of group(peerKeyShore)) {
    shoreBar.set(
      k,
      mean(members.map((p) => p.costPerClaim).filter((v): v is number => v !== null)),
    )
  }

  /** The month billed under the job title's minimum (Person.volumeBars): the minimum, else null. */
  const lowMonth = (p: Person, row: TouchMonthRow | undefined): VolumeBar | null => {
    if (!row) return null
    const b = p.volumeBars?.[row.month]
    const billed = billedOf(row)
    return b && billed > 0 && isLowVolume(billed, b.bar) ? b : null
  }

  // Monthly bars: peer key → rule → one average per month index.
  const monthIx = new Map(months.map((m, i) => [m, i]))
  const monthBar = new Map<string, Map<ScoreKey, (number | null)[]>>()
  for (const [k, members] of group(peerKey)) {
    const acc = new Map<ScoreKey, number[][]>()
    for (const rule of MONTH_RULES) {
      if (rule.bar !== 'month') continue
      acc.set(
        rule.key,
        months.map(() => []),
      )
    }
    for (const p of members) {
      for (const row of p.months) {
        const i = monthIx.get(row.month)
        if (i === undefined) continue
        if (lowMonth(p, row) !== null) continue
        for (const rule of MONTH_RULES) {
          if (rule.bar !== 'month') continue
          const v = rule.get(row)
          if (v !== null && Number.isFinite(v)) acc.get(rule.key)![i]!.push(v)
        }
      }
    }
    const out = new Map<ScoreKey, (number | null)[]>()
    for (const [key, arr] of acc) out.set(key, arr.map(mean))
    monthBar.set(k, out)
  }

  /** The bar one rule is measured against for this person in this month. */
  function barFor(p: Person, rule: MonthRule, i: number): number | null {
    if (rule.bar === 'month') return monthBar.get(peerKey(p))?.get(rule.key)?.[i] ?? null
    if (rule.key === 'costPerClaim') {
      const s = shoreBar.get(peerKeyShore(p))
      if (s !== null && s !== undefined) return s
    }
    const v = periodBar.get(peerKey(p))?.get(rule.key) ?? null
    if (v === null) return null
    return rule.perMonth ? v / Math.max(1, months.length) : v
  }

  /** One rule's points for one person-month, or null when there is no figure. */
  function rulePoints(
    p: Person,
    rule: MonthRule,
    row: TouchMonthRow | undefined,
    i: number,
  ): { pts: number; value: number; bar: number } | null {
    if (!row || p.terminated) return null
    const v = rule.get(row)
    if (v === null || !Number.isFinite(v)) return null
    const bar = barFor(p, rule, i)
    if (bar === null || !Number.isFinite(bar)) return null
    const beats = rule.lowerIsBetter ? v < bar : v > bar
    return { pts: beats ? rule.pts : 0, value: v, bar }
  }

  /** The whole month's score, or null when nothing at all could be scored. */
  function score(p: Person, row: TouchMonthRow | undefined, i: number): MonthScore | null {
    if (p.terminated) return null
    let got = 0
    let live = 0
    const gaps: string[] = []
    let any = false
    for (const rule of MONTH_RULES) {
      const r = rulePoints(p, rule, row, i)
      if (!r) {
        gaps.push(rule.label)
        continue
      }
      any = true
      got += r.pts
      live += rule.pts
    }
    if (!any) return null
    const penalty = monthPenalty(row)
    if (penalty) got += PENALTY.pts
    const pen = penalty ? { penalty: true } : {}
    const bar = lowMonth(p, row)
    if (bar !== null && row) {
      return { got: 0, live, gaps, ...pen, lowVolume: { computed: got, claims: billedOf(row), bar: bar.bar, avg: bar.avg } }
    }
    return { got, live, gaps, ...pen }
  }

  return { score, rulePoints, barFor, lowMonth, monthIndex: monthIx }
}

export type MonthScorer = ReturnType<typeof monthScorer>

/**
 * The −3 penalty for one month: claims billed vs resolved % under 50%, on STANDARD billed
 * claims (resolved claims are never weighted), as `scorecard()` tests the period.
 * Absolute, not against peers. No billed claims, no figure, no penalty.
 */
export function monthPenalty(row: TouchMonthRow | undefined): boolean {
  if (!row) return false
  const v = divide(row.resolvedClaims, row.billedClaims)
  return v !== null && Number.isFinite(v) && v < PENALTY.threshold
}

/**
 * Monthly scores (or one line's monthly points) averaged over the months, weighted by
 * the billed claims in each — the figure at the end of a month-by-month row.
 *
 * This was a SUM (Jun +9, Jul +8.5, Aug +7 → +24.5), a number on no scale anyone
 * scores against. Shayna asked for a weighted average on 2026-09-25, so the row's last
 * figure reads on the same −3…+11 scale as each month; Yehuda chose billed claims as
 * the weight, so a 900-claim month counts for more than a 40-claim one and a thin
 * month cannot swing it.
 *
 * Months with no score are left out, not counted as zero. If every scored month has
 * no billed claims the weights say nothing, and it falls back to the plain average
 * rather than showing nothing for a person who was scored.
 */
export function weightedMonthAverage(
  values: readonly (number | null)[],
  billed: readonly number[],
): number | null {
  return monthAverage(values, billed, 'billed')
}

/**
 * How the month row's last figure averages its months. 'billed' (the default, decided
 * 2026-09-25) weights each month by its billed claims; 'plain' gives every scored month
 * the same say. One constant, so the meeting can flip it — the grid's labels read it.
 *
 * This is ONLY the month grid's row average. The period score is not an average of
 * monthly scores at all: it is `scorecard()` on the range's pooled figures
 * (MEASURE-RULES §10.8), and neither setting changes it.
 */
export type MonthWeighting = 'billed' | 'plain'
export const MONTH_AVERAGE_WEIGHTING: MonthWeighting = 'billed'

/** The months' values averaged under `weighting`. Unscored months are left out. */
export function monthAverage(
  values: readonly (number | null)[],
  billed: readonly number[],
  weighting: MonthWeighting = MONTH_AVERAGE_WEIGHTING,
): number | null {
  if (weighting === 'plain') {
    const got = values.filter((v): v is number => v !== null)
    return got.length ? got.reduce((a, b) => a + b, 0) / got.length : null
  }
  let num = 0
  let den = 0
  let sum = 0
  let n = 0
  values.forEach((v, i) => {
    if (v === null) return
    const w = Math.max(0, billed[i] ?? 0)
    num += v * w
    den += w
    sum += v
    n += 1
  })
  if (n === 0) return null
  return den > 0 ? num / den : sum / n
}
